import type { PaginatedResult } from '../types/api';
import { getAccessToken } from '../auth/tokenStorage';

import { expireSession, refreshAccessToken } from './refreshAccessToken';
import { unwrapNestedData } from './unwrap';

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';

export class ApiError extends Error {
    status: number;
    body?: unknown;

    constructor(message: string, status: number, body?: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

/**
 * `fetch` against the API with the session's token. A 401 refreshes the token
 * and tries once more; a second 401 ends the session.
 */
async function authorizedFetch(path: string, options: RequestInit, retry: boolean): Promise<Response> {
    const headers = new Headers(options.headers);
    /** FormData must keep the browser-generated multipart boundary. */
    const isFormData = options.body instanceof FormData;
    if (!headers.has('Content-Type') && options.body && !isFormData) {
        headers.set('Content-Type', 'application/json');
    }
    const token = getAccessToken();
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    const res = await fetch(`${baseUrl}${path}`, { ...options, headers });

    if (res.status === 401) {
        if (retry) {
            const newToken = await refreshAccessToken();
            if (newToken) {
                return authorizedFetch(path, options, false);
            }
        }
        expireSession();
        throw new ApiError('Unauthorized', 401);
    }
    return res;
}

/** Reads the error body of a failed response into an `ApiError`. */
async function toApiError(res: Response): Promise<ApiError> {
    const text = await res.text();
    let json: unknown;
    try {
        json = text ? (JSON.parse(text) as unknown) : null;
    } catch {
        json = text;
    }
    const message =
        (json as { message?: string })?.message ??
        (Array.isArray((json as { message?: string[] })?.message)
            ? (json as { message: string[] }).message.join(', ')
            : res.statusText);
    return new ApiError(message, res.status, json);
}

export async function apiRequest<T>(
    path: string,
    options: RequestInit = {},
    retry = true,
): Promise<T> {
    const res = await authorizedFetch(path, options, retry);
    if (!res.ok) {
        throw await toApiError(res);
    }
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : null;
    return unwrapNestedData<T>(json);
}

export type ApiDownload = {
    blob: Blob;
    /** The name the API suggests in `Content-Disposition`, when it sends one. */
    filename?: string;
    headers: Headers;
};

/** A file from the API, such as a CSV export, rather than JSON. */
export async function apiDownload(path: string): Promise<ApiDownload> {
    const res = await authorizedFetch(path, {}, true);
    if (!res.ok) {
        throw await toApiError(res);
    }
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const filename = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
    return { blob: await res.blob(), filename, headers: res.headers };
}

export async function apiList<T>(
    path: string,
    params: Record<string, string | number | undefined>,
): Promise<PaginatedResult<T>> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            search.set(key, String(value));
        }
    });
    const qs = search.toString();
    const result = await apiRequest<PaginatedResult<T>>(qs ? `${path}?${qs}` : path);
    if (Array.isArray(result)) {
        return { data: result, total: result.length };
    }
    return result;
}

export async function apiPublicRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers = new Headers(options.headers);
    if (!headers.has('Content-Type') && options.body) {
        headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(`${baseUrl}${path}`, { ...options, headers });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : null;
    if (!res.ok) {
        throw new ApiError(res.statusText, res.status, json);
    }
    return unwrapNestedData<T>(json);
}
